// Slack export importer — client side.
//
// Given a File (the ZIP the user picked in the UI), parse it in the
// browser using JSZip and drive the server through the phased-import
// endpoint (api/slack-import.js).
//
// Usage:
//   const summary = await importSlackZip(zipFile, {
//     onProgress: (stage, done, total) => setStatus(...),
//   });
//   // summary: { session_id, counts: { users, channels, messages, skipped } }
//
// Phases:
//   1) init         → get session_id
//   2) users.json   → POST action=users
//   3) channels.json → POST action=channels
//   4) walk each channel folder's day-JSON files, batch messages
//      by CHUNK_SIZE, POST action=messages
//   5) commit
//
// All requests use authFetch so the Keycloak bearer is set correctly.
// jszip is a dep of this repo already (used by proposal-library
// prep) — no new deps.

import JSZip from 'jszip';
import { authFetch } from '../auth.js';

const CHUNK_SIZE = 200;

async function post(action, body) {
  const res = await authFetch(`/api/slack-import?action=${action}`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify(body),
  });
  if (!res.ok) {
    let msg = res.statusText;
    try { msg = (await res.json()).error || msg; } catch {}
    throw new Error(`slack-import ${action}: ${msg}`);
  }
  return res.json();
}

function chunk(arr, size) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

// Slack's per-day channel files sit at `<channel_name>/YYYY-MM-DD.json`.
// Return them grouped by channel name.
function groupDayFiles(zip) {
  const groups = new Map(); // channelName → [{ name, file }]
  zip.forEach((relativePath, file) => {
    if (file.dir) return;
    const parts = relativePath.split('/').filter(Boolean);
    if (parts.length !== 2) return;
    if (!parts[1].endsWith('.json')) return;
    if (parts[1] === 'users.json' || parts[1] === 'channels.json' || parts[1] === 'integration_logs.json') return;
    const ch = parts[0];
    if (!groups.has(ch)) groups.set(ch, []);
    groups.get(ch).push({ name: parts[1], file });
  });
  return groups;
}

async function readJson(file) {
  const txt = await file.async('string');
  try { return JSON.parse(txt); } catch { return null; }
}

export async function importSlackZip(zipFile, { onProgress } = {}) {
  const zip = await JSZip.loadAsync(zipFile);
  const report = (stage, done, total) => onProgress?.(stage, done, total);

  // Phase 1 — init.
  report('init', 0, 1);
  const { session_id } = await post('init', {});
  report('init', 1, 1);

  // Phase 2 — users.
  const usersJson = zip.file('users.json');
  if (usersJson) {
    const raw = await readJson(usersJson) || [];
    // Normalize to what the server expects.
    const users = raw.map(u => ({
      slack_id:  u.id,
      email:     u.profile?.email || null,
      name:      u.name,
      real_name: u.profile?.real_name || u.real_name || null,
      image_72:  u.profile?.image_72 || null,
    }));
    const batches = chunk(users, 500);
    for (let i = 0; i < batches.length; i += 1) {
      await post('users', { session_id, users: batches[i] });
      report('users', (i + 1) * 500, users.length);
    }
  }

  // Phase 3 — channels + groups (private channels).
  const chansJson  = zip.file('channels.json');
  const groupsJson = zip.file('groups.json');
  const allChans = [];
  for (const [handle, isPrivate] of [[chansJson, false], [groupsJson, true]]) {
    if (!handle) continue;
    const raw = await readJson(handle) || [];
    for (const c of raw) {
      allChans.push({
        slack_id:   c.id,
        name:       c.name,
        purpose:    c.purpose?.value || null,
        topic:      c.topic?.value || null,
        is_archived: !!c.is_archived,
        is_private:  isPrivate,
      });
    }
  }
  if (allChans.length) {
    const batches = chunk(allChans, 200);
    for (let i = 0; i < batches.length; i += 1) {
      await post('channels', { session_id, channels: batches[i] });
      report('channels', (i + 1) * 200, allChans.length);
    }
  }

  // Phase 4 — messages, per channel, per day file, batched.
  const dayGroups = groupDayFiles(zip);
  const totalChannels = dayGroups.size;
  let doneChannels = 0;
  let totalMsgs = 0, totalRoots = 0, totalReplies = 0, totalSkipped = 0;

  for (const [channelName, files] of dayGroups) {
    // Find the Slack channel id — read the first day file's first msg.
    // Slack's exports don't carry the channel id in per-day files, so
    // we cross-reference the channels.json we loaded.
    const chanMeta = allChans.find(c => c.name === channelName);
    if (!chanMeta) { doneChannels += 1; continue; }
    const channelSlackId = chanMeta.slack_id;

    // Sort files by date so the trigger-driven reply_count rollup
    // sees roots before their replies where possible.
    files.sort((a, b) => a.name.localeCompare(b.name));
    for (const { file } of files) {
      const raw = await readJson(file);
      if (!Array.isArray(raw)) continue;
      const batches = chunk(raw, CHUNK_SIZE);
      for (const batch of batches) {
        const out = await post('messages', {
          session_id,
          channel_slack_id: channelSlackId,
          messages: batch,
        });
        totalMsgs    += batch.length;
        totalRoots   += out.inserted_roots   || 0;
        totalReplies += out.inserted_replies || 0;
        totalSkipped += out.skipped          || 0;
      }
    }
    doneChannels += 1;
    report('messages', doneChannels, totalChannels);
  }

  // Phase 5 — commit.
  await post('commit', { session_id });
  report('done', 1, 1);

  return {
    session_id,
    counts: {
      users: allChans.length ? undefined : undefined, // set server-side; caller can /status if needed
      channels: allChans.length,
      messages_total:    totalMsgs,
      inserted_roots:    totalRoots,
      inserted_replies:  totalReplies,
      skipped:           totalSkipped,
    },
  };
}

// Optional convenience: fetch the current session status from the server.
export async function slackImportStatus(sessionId) {
  const res = await authFetch(`/api/slack-import?action=status&session_id=${encodeURIComponent(sessionId)}`);
  if (!res.ok) throw new Error(`status ${res.status}`);
  return res.json();
}
